/**
 * Agent Detail — one agent's full configuration, trust metrics, and recent
 * decisions.
 *
 * Reads GET /api/agentic/agents/{id} (backend/app/routes/agentic_fleet.py).
 * Read-only. Recent decisions link to the Decision card. On 403/404 →
 * NotEnabled (or "agent not found" for a bad id).
 */
import { useParams, Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, Bot } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Heading,
  Icon,
  PageHeader,
  Section,
  Spinner,
  Stack,
  Text,
} from '@smms/design-system';
import {
  getAgent,
  type AgentTrustMetric,
  type AgentDecisionSummary,
  type RiskClassification,
} from '../api/agentic';
import { isNotEntitled } from '../api/client';
import NotEnabled from '../components/NotEnabled';

const PHASE_BADGE: Record<string, 'neutral' | 'info' | 'primary' | 'success'> = {
  observer: 'neutral',
  recommendation: 'info',
  supervised: 'primary',
  full_autonomous: 'success',
};

const RISK_BADGE: Record<RiskClassification, 'success' | 'warning' | 'danger'> = {
  routine: 'success',
  non_routine: 'warning',
  safety_critical: 'danger',
};

function humanize(s: string): string {
  return s.replaceAll('_', ' ');
}

function formatWhen(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function pct(fraction: number | null): string {
  return fraction == null ? '—' : `${Math.round(fraction * 100)}%`;
}

function JsonBlock({ value }: { value: unknown }) {
  if (value == null || (typeof value === 'object' && Object.keys(value).length === 0)) {
    return (
      <Text variant="body-sm" tone="muted">
        —
      </Text>
    );
  }
  return (
    <pre className="overflow-x-auto rounded-md bg-elevated p-3 text-xs text-text">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

export default function AgentDetailPage() {
  const { agentId = '' } = useParams();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['agentic', 'agent', agentId],
    queryFn: () => getAgent(agentId),
    enabled: Boolean(agentId),
  });

  const backLink = (
    <Link
      to="/agents"
      className="inline-flex items-center gap-1 text-sm text-muted transition-colors hover:text-text"
    >
      <Icon as={ArrowLeft} size="sm" />
      Back to Agents
    </Link>
  );

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" data-testid="agent-loading">
        <Spinner size="lg" label="Loading agent" />
      </div>
    );
  }

  if (error) {
    if (isNotEntitled(error)) return <NotEnabled />;
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-8">
        <EmptyState
          variant="error"
          icon={<Icon as={AlertTriangle} size="lg" />}
          title="Couldn't load this agent"
          description="It may not exist for your tenant, or something went wrong. Try again."
          action={<Button onClick={() => void refetch()}>Retry</Button>}
        />
      </div>
    );
  }

  if (!data) return <NotEnabled />;

  const { config, trust, recent_decisions } = data;

  return (
    <div className="mx-auto w-full max-w-4xl p-6">
      <Stack gap="lg">
        {backLink}
        <PageHeader
          title={humanize(data.agent_id)}
          description={`Config version ${config.config_version} · updated ${formatWhen(
            config.updated_at,
          )}`}
          actions={<Icon as={Bot} size="md" className="text-primary" />}
        />

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card padding="md">
            <Stack gap="sm">
              <Heading level={2} className="text-base">
                Autonomy & thresholds
              </Heading>
              <JsonBlock value={config.autonomy_settings} />
              <JsonBlock value={config.approval_thresholds} />
            </Stack>
          </Card>
          <Card padding="md">
            <Stack gap="sm">
              <Heading level={2} className="text-base">
                Scope & integrity
              </Heading>
              <JsonBlock value={config.capability_scope} />
              <JsonBlock value={config.integrity_controls} />
            </Stack>
          </Card>
        </div>

        <Section>
          <Stack gap="sm">
            <Heading level={2} className="text-base">
              Trust metrics
            </Heading>
            {trust.length === 0 ? (
              <Card padding="md">
                <Text variant="body-sm" tone="muted">
                  No trust metrics yet — agent is still observing.
                </Text>
              </Card>
            ) : (
              <Card padding="none">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="text-muted">
                      <th className="px-4 py-2 font-medium">Decision type</th>
                      <th className="px-4 py-2 font-medium">Phase</th>
                      <th className="px-4 py-2 font-medium">Decisions</th>
                      <th className="px-4 py-2 font-medium">Override</th>
                      <th className="px-4 py-2 font-medium">Avg conf.</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {trust.map((t: AgentTrustMetric) => (
                      <tr key={t.decision_type}>
                        <td className="px-4 py-2">{humanize(t.decision_type)}</td>
                        <td className="px-4 py-2">
                          <Badge variant={PHASE_BADGE[t.current_phase] ?? 'neutral'} size="sm">
                            {humanize(t.current_phase)}
                          </Badge>
                        </td>
                        <td className="px-4 py-2">{t.total_decisions}</td>
                        <td className="px-4 py-2">{pct(t.override_rate)}</td>
                        <td className="px-4 py-2">
                          {t.avg_confidence != null ? `${Math.round(t.avg_confidence)}%` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            )}
          </Stack>
        </Section>

        <Section>
          <Stack gap="sm">
            <Heading level={2} className="text-base">
              Recent decisions
            </Heading>
            {recent_decisions.length === 0 ? (
              <Card padding="md">
                <Text variant="body-sm" tone="muted">
                  No decisions recorded yet.
                </Text>
              </Card>
            ) : (
              <Card padding="none">
                <ul className="divide-y divide-border">
                  {recent_decisions.map((d: AgentDecisionSummary) => (
                    <li key={d.id}>
                      <Link
                        to={`/decisions/${d.id}`}
                        className="flex flex-wrap items-center gap-3 px-4 py-3 transition-colors hover:bg-elevated"
                      >
                        <div className="min-w-0 flex-1">
                          <Text variant="body-sm" className="font-medium">
                            {humanize(d.decision_type)}
                          </Text>
                          <Text variant="caption" tone="muted">
                            {d.autonomy_phase} · {formatWhen(d.created_at)}
                          </Text>
                        </div>
                        <Badge variant={RISK_BADGE[d.risk_classification]} size="sm">
                          {humanize(d.risk_classification)}
                        </Badge>
                        <Text variant="mono" tone="muted">
                          {d.calibrated_confidence ?? d.raw_confidence}%
                        </Text>
                      </Link>
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </Stack>
        </Section>
      </Stack>
    </div>
  );
}
